import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Package, Users as UsersIcon, UserCircle2, ScanBarcode, Receipt,
  LogOut, Boxes, Building2, Truck, Wallet, Gift, MessageSquare, CheckSquare,
  Upload, Printer, BarChart3, FileDown, ChevronDown, ChevronRight, RefreshCw,
  FileText, Zap, ClipboardList, History, UserCheck, Settings, ListChecks,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Role-aware navigation. Dashboard + BI are Super Admin ONLY per FIX 11.
const SECTIONS = [
  { key: "main", label: null, items: [
    // Dashboard visible only to super_admin per FIX 10/11
    { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin"], testid: "nav-dashboard" },
    // Admin lands directly on Bills (operations screen). POS Terminal billing screen available to cashiers/admins/super-admin.
    { to: "/pos", label: "POS Terminal", icon: ScanBarcode, all: true, testid: "nav-pos" },
  ]},
  { key: "retail", label: "Retail", items: [
    { to: "/bills", label: "Sales Bills", icon: Receipt, all: true, testid: "nav-bills" },
    { to: "/returns", label: "Returns", icon: RefreshCw, all: true, testid: "nav-returns" },
    { to: "/products", label: "Products", icon: Package, roles: ["super_admin", "admin"], testid: "nav-products" },
    { to: "/customers", label: "Customers", icon: UserCircle2, all: true, testid: "nav-customers" },
  ]},
  { key: "supply", label: "Supply", items: [
    { to: "/parties", label: "Parties", icon: Truck, roles: ["super_admin", "admin"], testid: "nav-parties" },
    { to: "/purchase-bills", label: "Purchase Bills", icon: FileDown, roles: ["super_admin", "admin"], testid: "nav-purchase" },
    { to: "/purchase-returns", label: "Purchase Returns", icon: RefreshCw, roles: ["super_admin", "admin"], testid: "nav-purchase-returns" },
    { to: "/expenses", label: "Expenses", icon: Wallet, perm: "expense_manage", testid: "nav-expenses" },
  ]},
  { key: "workforce", label: "Workforce", items: [
    { to: "/staff", label: "Staff & Attendance", icon: UserCheck, roles: ["super_admin", "admin"], testid: "nav-staff" },
    { to: "/credits", label: "Customer Credits", icon: Wallet, roles: ["super_admin", "admin"], testid: "nav-credits" },
  ]},
  { key: "engage", label: "Engage", items: [
    { to: "/loyalty", label: "Loyalty", icon: Gift, roles: ["super_admin"], testid: "nav-loyalty" },
    { to: "/campaigns", label: "WhatsApp", icon: MessageSquare, perm: "whatsapp_campaign_create", testid: "nav-campaigns" },
    { to: "/templates", label: "Templates", icon: FileText, roles: ["super_admin", "admin"], testid: "nav-templates" },
    { to: "/triggers", label: "Auto Triggers", icon: Zap, roles: ["super_admin"], testid: "nav-triggers" },
    { to: "/approvals", label: "Approval Queue", icon: CheckSquare, roles: ["super_admin"], testid: "nav-approvals" },
  ]},
  { key: "ops", label: "Operations", items: [
    { to: "/bulk-import", label: "Bulk Import", icon: Upload, roles: ["super_admin"], testid: "nav-import" },
    { to: "/import-jobs", label: "Tally Import Jobs", icon: ListChecks, roles: ["super_admin", "admin"], testid: "nav-import-jobs" },
    { to: "/barcode-labels", label: "Barcode Labels", icon: Printer, perm: "barcode_print", testid: "nav-barcode" },
    // POS TERMINAL FEATURE — ON HOLD, DO NOT DELETE
    // { to: "/pos-terminals", label: "POS Terminals", icon: Terminal, perm: "pos_create", testid: "nav-terminals" },
    { to: "/branches", label: "Branches", icon: Building2, roles: ["super_admin"], testid: "nav-branches" },
    { to: "/users", label: "Users & Roles", icon: UsersIcon, roles: ["super_admin"], testid: "nav-users" },
    { to: "/settings", label: "Org Settings", icon: Settings, roles: ["super_admin"], testid: "nav-settings" },
    { to: "/settings/bill-builder", label: "Bill Template Builder", icon: FileText, roles: ["super_admin"], testid: "nav-bill-builder" },
    { to: "/settings/label-builder", label: "Label Template Builder", icon: Printer, roles: ["super_admin"], testid: "nav-label-builder" },
  ]},
  { key: "admin", label: "Insights", items: [
    // BI & Reports & Audit Logs are Super Admin only
    { to: "/bi", label: "BI Dashboard", icon: BarChart3, roles: ["super_admin"], testid: "nav-bi" },
    { to: "/reports", label: "Reports", icon: ClipboardList, roles: ["super_admin"], testid: "nav-reports" },
    { to: "/audit-logs", label: "Audit Logs", icon: History, roles: ["super_admin"], testid: "nav-audit" },
  ]},
];

export default function Sidebar({ onNavigate }) {
  const { user, organisation, branches, activeBranchId, chooseBranch, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem("sidebar_collapsed");
      if (raw) return JSON.parse(raw);
    } catch {}
    return SECTIONS.reduce((acc, s) => { if (s.label) acc[s.key] = true; return acc; }, {});
  });
  const [hovering, setHovering] = useState(null);

  const toggle = (key) => {
    const next = { ...collapsed, [key]: !collapsed[key] };
    setCollapsed(next);
    localStorage.setItem("sidebar_collapsed", JSON.stringify(next));
  };

  const visible = (it) => {
    if (it.all) return true;
    if (it.roles && it.roles.includes(user.role)) return true;
    if (it.perm && hasPerm(it.perm)) return true;
    return false;
  };
  const visibleBranches = user.role === "super_admin" ? branches : branches.filter((b) => user.branch_ids?.includes(b.id));

  return (
    <aside className="w-64 bg-slate-900 text-slate-300 flex-shrink-0 min-h-screen flex flex-col" data-testid="sidebar">
      <div className="px-5 py-4 border-b border-slate-800 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Boxes className="w-6 h-6 text-white" strokeWidth={2} />
          <div className="min-w-0">
            <div className="text-white font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>eaassy</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 truncate">{organisation?.name}</div>
          </div>
        </div>
        {visibleBranches.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Branch</div>
            <Select value={activeBranchId} onValueChange={chooseBranch}>
              <SelectTrigger className="h-8 rounded-none bg-slate-800 border-slate-700 text-white text-xs" data-testid="branch-switcher">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {visibleBranches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto sidebar-scroll px-2 py-2" data-testid="sidebar-nav">
        {SECTIONS.map((section) => {
          const items = section.items.filter(visible);
          if (items.length === 0) return null;
          const isCollapsed = !!collapsed[section.key];
          const isHovered = hovering === section.key;
          const showItems = !isCollapsed || isHovered;
          return (
            <div
              key={section.key}
              className="mb-1"
              onMouseEnter={() => isCollapsed && setHovering(section.key)}
              onMouseLeave={() => setHovering(null)}
            >
              {section.label && (
                <button onClick={() => toggle(section.key)} data-testid={`section-${section.key}`}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-slate-300 transition-colors">
                  <span>{section.label}</span>
                  {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
              <div className={`overflow-hidden transition-all duration-200 ${showItems ? "max-h-96" : "max-h-0"}`}>
                {items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={it.testid}
                    onClick={() => { if (onNavigate) onNavigate(); }}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                      isActive ? "bg-white text-slate-900 font-semibold" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`}>
                    <it.icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                    <span className="truncate">{it.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="px-4 py-3 border-t border-slate-800 flex-shrink-0">
        <div className="mb-2" data-testid="sidebar-user">
          <div className="text-sm text-white font-medium truncate">{user.name}</div>
          <div className="text-xs text-slate-500 capitalize">{user.role.replace("_", " ")}</div>
        </div>
        <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </aside>
  );
}
